/**
 * Присъедини… — saying that a row of Адреси за заключване was one of the firm's places all
 * along (#434): its old bookings, its callers' addresses, joined to the place by reference.
 *
 * The places it could be come first, best first by why — joined before, the same address,
 * another entrance, similar words, a name, merely near — with how far each is and a warning
 * when its point is shared by others, as an importer's whole street on one spot leaves them.
 * Their order is frozen when the dialog opens; a search adds what the words find below.
 *
 * Under it, every member of the row, ticked by `defaultTicked` against the chosen door: what is
 * still under way, already locked or another town away cannot be ticked, and what is more than
 * a short walk off starts unticked, to be looked at. Grouping is a reading of words, so the
 * administrator sees each one.
 *
 * Opened from the suggestion on a row, the place suggested is chosen already, the ticks measured
 * from its door; the administrator still sees every other candidate and every member before
 * anything is joined.
 *
 * The join's id is made when the dialog opens, so pressing Присъедини twice — or a request the
 * browser gave up on — joins once. Nothing anybody typed is rewritten; the join can be undone
 * from the address's history, and the notice after it offers that at once.
 *
 * The buttons are the dialog's footer, outside the part that scrolls, so a row of hundreds of
 * bookings never hides Присъедини below them; beside them it says which place it joins to,
 * and that place stays listed even when a search no longer finds it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@taxi/api-client';

import { placesApi, type SimilarPlace } from '../places/api';
import { newId } from '../settings/api';
import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button, Dialog, EmptyState, Input, Pill, Table, Td, Th, Tr } from '../ui';
import {
  defaultTicked,
  joinRequest,
  memberGroup,
  reasonKey,
  tickedIds,
  type JoinRow,
  type MemberGroup,
  type ImportedView,
  type MemberTick,
  type PlaceJoinResult,
  type UnplacedMember,
} from './addressesQuery';
import { reportsApi, type AddressToLock, type UnplacedMembers } from './api';
import type { Period } from './period';

const GROUPS: readonly MemberGroup[] = ['inPeriod', 'outsidePeriod', 'callersAddresses'];

export function JoinDialog({
  open,
  row,
  period,
  from,
  to,
  source,
  view,
  prefer,
  onClose,
  onJoined,
  onGone,
}: {
  open: boolean;
  row: AddressToLock | null;
  /** The view of Внесени the row was listed under. */
  view: ImportedView;
  /** A place to start chosen — the row's suggestion, when it was opened from that. */
  prefer: string | null;
  /** The period the list was read over, for the members to be read the same way. */
  period: Period;
  /** The days the answer said it covered. */
  from: string | null;
  to: string | null;
  source: JoinRow['source'];
  onClose: () => void;
  onJoined: (result: PlaceJoinResult, line: string) => void;
  /** The row has nothing in it any more — locked, or joined, since the list was read — with the words saying so. */
  onGone: (message: string) => void;
}) {
  // Mounted afresh each time it opens, dialog and all, so a new join gets a new id and the
  // row's members now — and the form's state is at hand for the footer's buttons.
  return open && row !== null ? (
    <JoinForm
      row={row}
      period={period}
      view={view}
      prefer={prefer}
      join={{ key: row.key, line: row.line, source, from, to }}
      onClose={onClose}
      onJoined={onJoined}
      onGone={onGone}
    />
  ) : null;
}

function JoinForm({
  row,
  period,
  view,
  prefer,
  join,
  onClose,
  onJoined,
  onGone,
}: {
  row: AddressToLock;
  period: Period;
  view: ImportedView;
  prefer: string | null;
  join: JoinRow;
  onClose: () => void;
  onJoined: (result: PlaceJoinResult, line: string) => void;
  onGone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [joinId] = useState(() => newId());
  const [members, setMembers] = useState<UnplacedMembers | null>(null);
  const [candidates, setCandidates] = useState<SimilarPlace[] | null>(null);
  const [searched, setSearched] = useState<SimilarPlace[]>([]);
  const [typed, setTyped] = useState('');
  const [chosen, setChosen] = useState<SimilarPlace | null>(null);
  const [ticks, setTicks] = useState<MemberTick[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Read through a ref: the caller's callback is a fresh closure on every render, and the read
  // below must run once, not again on each.
  const gone = useRef(onGone);

  useEffect(() => {
    gone.current = onGone;
  });

  const asked = useMemo(
    () => ({ building: row.building, entrance: row.entrance ?? null, longitude: row.longitude ?? null, latitude: row.latitude ?? null }),
    [row],
  );

  // What the row is made of, and the places it could be: both read once, as the dialog opens.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [read, similar] = await Promise.all([
          reportsApi.unplacedMembers(period, row.key, join.source, view),
          placesApi.similar(asked),
        ]);

        if (cancelled) return;

        // The suggestion the dialog was opened from, chosen — when it is still among the places.
        const preferred = prefer === null ? undefined : similar.candidates.find((candidate) => candidate.placeId === prefer);

        setMembers(read);
        setTicks(defaultTicked(read.members, preferred ?? asked));
        setCandidates(similar.candidates);
        if (preferred !== undefined) setChosen(preferred);
      } catch (failure) {
        if (cancelled) return;

        const message = describe(failure, t, 'settings.couldNotLoad');

        // The page closes the dialog and says why there, where the words outlive it.
        if (failure instanceof ApiError && failure.problem.code === 'report.row_gone') gone.current(message);
        else setProblem(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [period, row.key, join.source, view, prefer, asked, t]);

  // The administrator's words, after a pause: what they find comes under the rest, which stay put.
  useEffect(() => {
    const words = typed.trim();

    if (words === '') return;

    let cancelled = false;

    const timer = setTimeout(() => {
      void placesApi.similar({ ...asked, q: words }).then(
        (answer) => {
          if (!cancelled) setSearched(answer.candidates.filter((candidate) => candidate.kind === 'Search'));
        },
        () => undefined,
      );
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [typed, asked]);

  const found = [...(candidates ?? []), ...(typed.trim() === '' ? [] : searched)];

  // The place chosen stays on screen whatever the search now finds: it is what Присъедини joins to.
  const listed = chosen === null || found.some((place) => place.placeId === chosen.placeId) ? found : [...found, chosen];

  function choose(place: SimilarPlace) {
    setChosen(place);

    // Measured from the door now, not the row's own point.
    if (members !== null) setTicks(defaultTicked(members.members, place));
  }

  function toggle(id: string) {
    setTicks((current) => current.map((tick) => (tick.id === id && !tick.disabled ? { ...tick, ticked: !tick.ticked } : tick)));
  }

  const picked = tickedIds(ticks);

  async function submit() {
    if (chosen === null || members === null || picked.size === 0) return;

    setBusy(true);
    setProblem(null);

    try {
      const result = await placesApi.join(chosen.placeId, joinRequest(joinId, join, members.members, picked));

      onJoined(result, chosen.line);
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  const byId = new Map(ticks.map((tick) => [tick.id, tick]));

  return (
    <Dialog
      open
      wide
      title={t('reports.addresses.joinTitle', { line: row.line })}
      onClose={onClose}
      actions={
        <>
          <span className="mr-auto text-secondary text-ink-2">
            {[
              members !== null
                ? t('reports.addresses.joinChosen', { count: formats.count(picked.size), total: formats.count(members.members.length) })
                : null,
              chosen !== null ? t('reports.addresses.joinTarget', { line: chosen.line }) : null,
            ]
              .filter((part) => part !== null)
              .join(' ')}
          </span>
          <Button variant="quiet" onClick={onClose}>
            {t('surcharges.cancelEdit')}
          </Button>
          <Button variant="primary" disabled={busy || chosen === null || picked.size === 0} onClick={() => void submit()}>
            {t('reports.addresses.joinConfirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-secondary text-ink-2">{t('reports.addresses.joinAbout')}</p>

        {problem !== null && <Banner tone="danger">{problem}</Banner>}

        <div className="flex flex-col gap-2">
          <div className="text-body font-semibold">{t('reports.addresses.joinCandidates')}</div>
          <Input
            type="search"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              // What the old words found goes at once, not when the new ones are answered.
              setSearched([]);
            }}
            placeholder={t('reports.addresses.joinSearch')}
            aria-label={t('reports.addresses.joinSearch')}
          />

          {candidates === null ? (
            problem === null && <EmptyState>{t('app.loading')}</EmptyState>
          ) : listed.length === 0 ? (
            <EmptyState>{t('reports.addresses.joinNoCandidates')}</EmptyState>
          ) : (
            <Table tight>
              <thead>
                <Tr>
                  <Th>{t('reports.addresses.address')}</Th>
                  <Th />
                  <Th numeric />
                </Tr>
              </thead>
              <tbody>
                {listed.map((place) => (
                  <Tr key={place.placeId} selected={chosen?.placeId === place.placeId} onClick={() => choose(place)}>
                    <Td wrap>
                      <div>{place.line}</div>
                      {place.names.length > 0 && <div className="text-secondary text-ink-3">{place.names.join(', ')}</div>}
                    </Td>
                    <Td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Pill tone={place.kind === 'Near' ? 'neutral' : 'info'} soft>
                          {t(`reports.addresses.reason.${reasonKey(place.kind, place.metres)}`)}
                        </Pill>
                        {place.sharesPointWith > 0 && (
                          <Pill tone="warn" soft>
                            {t('reports.addresses.sharesPoint', { n: place.sharesPointWith })}
                          </Pill>
                        )}
                      </span>
                    </Td>
                    <Td numeric>
                      {place.metres != null && t('reports.addresses.metres', { n: formats.count(place.metres) })}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {members !== null && (
          <div className="flex flex-col gap-2">
            <div className="text-body font-semibold">{t('reports.addresses.joinMembers')}</div>

            {GROUPS.map((group) => {
              const inGroup = members.members.filter((member) => memberGroup(member) === group);

              if (inGroup.length === 0) return null;

              return (
                <fieldset key={group} className="m-0 p-0 border-0 flex flex-col gap-1">
                  <legend className="text-secondary text-ink-2 mb-1">{t(`reports.addresses.${group}`)}</legend>
                  {inGroup.map((member) => (
                    <MemberLine key={member.id} member={member} tick={byId.get(member.id)} onToggle={() => toggle(member.id)} />
                  ))}
                  {group === 'outsidePeriod' && members.outsideTotal > inGroup.length && (
                    <p className="m-0 text-secondary text-ink-3">
                      {t('reports.addresses.outsideMore', { shown: formats.count(inGroup.length), total: formats.count(members.outsideTotal) })}
                    </p>
                  )}
                </fieldset>
              );
            })}
          </div>
        )}

      </div>
    </Dialog>
  );
}

/** One booking or address of the row, with its box. */
function MemberLine({ member, tick, onToggle }: { member: UnplacedMember; tick: MemberTick | undefined; onToggle: () => void }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const disabled = tick?.disabled ?? true;
  const why =
    member.why === 'Live'
      ? t('reports.addresses.live')
      : member.why === 'Linked'
        ? t('reports.addresses.linked')
        : disabled && tick?.metres != null
          ? t('reports.addresses.far', { n: formats.count(Math.round(tick.metres)) })
          : null;

  return (
    <label className="flex items-start gap-2 text-body">
      <input
        type="checkbox"
        className="mt-1 accent-brand"
        checked={tick?.ticked === true && !disabled}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="flex flex-col min-w-0">
        <span>
          {member.typed}
          {member.notes != null && member.notes !== '' && <span className="text-ink-3"> · {member.notes}</span>}
        </span>
        <span className="text-secondary text-ink-3">
          {[
            member.reference,
            member.caller ?? member.phone,
            formats.moment(member.at),
            tick?.metres != null && !disabled ? t('reports.addresses.metres', { n: formats.count(Math.round(tick.metres)) }) : null,
            why,
          ]
            .filter((part) => part != null && part !== '')
            .join(' · ')}
        </span>
      </span>
    </label>
  );
}
